import { LogLayer, LogLevelType } from 'loglayer'
import {
  getSimplePrettyTerminal,
  pastel,
} from '@loglayer/transport-simple-pretty-terminal'
import type { LoggerConfig } from './types'
import {
  isDeployed,
  isDev,
  isBrowser,
  LOG_LEVEL,
  DD_LOG_LEVEL,
  DD_SERVICE,
  DD_ENV,
  DD_VERSION,
  DD_RUM_SESSION_SAMPLE_RATE,
  DD_TRACE_SAMPLE_RATE,
  isTest,
} from './lib/constants'

const defaultConfig: LoggerConfig = {
  service: DD_SERVICE,
  env: DD_ENV,
  version: DD_VERSION,
  silent: isTest,
  level: DD_LOG_LEVEL as LogLevelType,
  ddSessionSampleRate: DD_RUM_SESSION_SAMPLE_RATE,
  ddTraceSampleRate: DD_TRACE_SAMPLE_RATE,
  datadogClientToken: isBrowser
    ? process.env.DATADOG_CLIENT_TOKEN ||
      process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ||
      // Yes, that's real. It's a public ingest-only token for the web-dupe-com RUM application
      'pub786b07930ac076aa821e0a0074a5ba8b'
    : undefined,
  datadogServerToken: undefined, // Browser never uses server token
}

export class Logger {
  private instance: LogLayer
  private config: LoggerConfig

  constructor(_config: LoggerConfig = {}, logger?: LogLayer) {
    // Merge args with defaults
    this.config = { ...defaultConfig, ..._config }

    if (logger) {
      this.instance = logger
    } else {
      const transports = [
        getSimplePrettyTerminal({
          id: 'console',
          enabled: !this.config.json && (!isDeployed || isBrowser),
          theme: pastel,
          runtime: 'browser',
          viewMode: 'message-only',
          includeDataInBrowserConsole: true,
          collapseArrays: false,
          level: LOG_LEVEL as LogLevelType,
        }),
      ]

      this.instance = new LogLayer({
        muteContext: isDev,
        // In browser, add error to Datadog RUM
        errorSerializer: (err) => {
          // Duck-type check for AxiosError without importing axios
          if ((err as any)?.isAxiosError === true) {
            delete (err as any).config
          }
          // Background add the error to Datadog RUM
          ;(async () => {
            try {
              const { datadogRum } = await import('@datadog/browser-rum')
              datadogRum.addError(err)
            } catch {
              // noop
            }
          })()

          // Pass the error on
          return err
        },
        transport: transports,
      })

      // Initialize Datadog transport
      // This is async due to dynamic imports of runtime specific packages
      void this.initializeDatadog()
        .then(({ transport }) => {
          if (transport) {
            this.instance = this.instance.withFreshTransports([
              ...transports,
              transport,
            ])
          }
        })
        .catch((error) => {
          this.error('Error initializing Datadog', error)
        })

      this.instance.setLevel((this.config.level as LogLevelType) || LOG_LEVEL)
      if ((isTest && !this.config.json) || this.config.silent) {
        this.instance.disableLogging()
      } else {
        this.instance.enableLogging()
      }

      this.trace('Logger ready', {
        service: this.config.service,
        env: this.config.env,
        version: this.config.version,
        silent: this.config.silent,
        level: this.config.level,
        datadogClientToken: !!this.config.datadogClientToken,
      })
    }
  }

  private async initializeDatadog() {
    let transport = undefined

    const { initializeDatadogBrowser } = await import('./lib/browser')
    const { transport: browserTransport } = await initializeDatadogBrowser({
      ...this.config,
      isDeployed,
      isDev,
    })
    transport = browserTransport

    if (!transport) {
      this.debug('No Datadog transport initialized')
      return {}
    }

    return { transport }
  }

  trace(message: string, params?: Record<string, any>) {
    if (params) {
      this.instance.withMetadata(params).trace(message)
    } else {
      this.instance.trace(message)
    }
  }

  debug(message: string, params?: Record<string, any>) {
    if (params) {
      this.instance.withMetadata(params).debug(message)
    } else {
      this.instance.debug(message)
    }
  }

  info(message: string, params?: Record<string, any>) {
    if (params) {
      this.instance.withMetadata(params).info(message)
    } else {
      this.instance.info(message)
    }
  }

  warn(message: string, params?: Record<string, any>) {
    if (params) {
      this.instance.withMetadata(params).warn(message)
    } else {
      this.instance.warn(message)
    }
  }

  error(message: string, params?: Record<string, any>) {
    // Log the error raw for maximum debugability
    if (!this.config.silent) {
      console.error(message, params)
    }

    // Then log it through the logger for external transport
    if (params) {
      if (params.error || params.err) {
        const { error, err, ...rest } = params

        this.instance
          .withError(error || err)
          .withMetadata(rest)
          .error(message)
      } else if (params instanceof Error) {
        this.instance.withError(params).error(message)
      } else {
        this.instance.withMetadata(params).error(message)
      }
    } else {
      this.instance.error(message)
    }
  }

  child(type: string, data?: Record<string, any>) {
    const childLogger = this.instance.child().withContext({
      type,
      ...data,
    })
    return new Logger(this.config, childLogger)
  }
}

const loggerCache = new Map<string, WeakRef<Logger>>()

const getLogger = (
  name?: string,
  config?: LoggerConfig,
  logger?: LogLayer
): Logger => {
  const key = `${name || 'default'}-${JSON.stringify(config) || 'default'}-${logger ? 'child' : 'root'}`
  if (loggerCache.has(key)) {
    const _logger = loggerCache.get(key)?.deref()
    if (_logger) {
      return _logger
    } else {
      loggerCache.delete(key)
    }
  }
  const _logger = new Logger(config, logger)
  loggerCache.set(key, new WeakRef(_logger))
  return _logger
}

// pinoLogger stub for browser — doesn't do anything but exported for API compatibility
export const pinoLogger = {
  info: () => {},
  debug: () => {},
  warn: () => {},
  error: () => {},
  trace: () => {},
  child: () => pinoLogger,
} as any

export default getLogger
